import React, { useEffect, useMemo, useState } from "react";
import { Users, X } from "lucide-react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  useMediaQuery,
  Paper,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import {
  useCreateUserMutation,
  useEditUserMutation,
} from "../../redux/admin/adminApi";
import {
  ROLES,
  getPermissions,
  hasUserPermission,
  clampAppPermissionsByActor,
} from "../../utils/rbacHelper";
import { normalizeUserId } from "../modals/permissionsDefaults";

const ROLE_LABELS = {
  [ROLES.MANAGER]: "Manager",
  [ROLES.HR]: "HR",
  [ROLES.OFFICE_EMPLOYEE]: "Office Employee",
  [ROLES.FIELD_EMPLOYEE]: "Field Employee",
};

const ORG_CREATABLE_BY_ROLE = {
  [ROLES.SUPER_ADMIN]: [
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
  ],
  [ROLES.MANAGER]: [
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.USER,
    ROLES.VIEWER,
  ],
  [ROLES.HR]: [
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.USER,
    ROLES.VIEWER,
  ],
};

const PERMISSION_GROUPS = [
  {
    title: "View Permissions",
    items: [
      { key: "viewDashboard", label: "View Dashboard" },
      { key: "viewUsers", label: "View Users" },
      { key: "viewDevices", label: "View Devices" },
      { key: "viewAssets", label: "View Assets" },
      { key: "viewEvents", label: "View Events" },
      { key: "viewMap", label: "View Live Map" },
      { key: "viewProtocols", label: "View Protocols" },
      { key: "manageSensors", label: "Manage Sensors" },
    ],
  },
  {
    title: "User Management",
    items: [
      { key: "createUser", label: "Create Users" },
      { key: "editUser", label: "Edit Users" },
      { key: "deleteUser", label: "Delete Users" },
      { key: "restoreUser", label: "Restore Users" },
      { key: "resetPassword", label: "Reset Password" },
      { key: "assignUserToGA", label: "Assign User To Dealer" },
      { key: "removeManagedUser", label: "Remove Managed User" },
      { key: "loginAsUser", label: "Login As User" },
    ],
  },
  {
    title: "Device Management",
    items: [
      { key: "createDevice", label: "Add Device" },
      { key: "editDevice", label: "Edit Device" },
      { key: "deleteDevice", label: "Delete Device" },
      { key: "assignDevice", label: "Assign Device" },
      { key: "sendCommands", label: "Send Commands" },
      { key: "manageGeofences", label: "Manage Geofences" },
    ],
  },
  {
    title: "Assets And Credits",
    items: [
      { key: "createAsset", label: "Add Assets" },
      { key: "editAsset", label: "Edit Assets" },
      { key: "deleteAsset", label: "Delete Assets" },
      { key: "restoreAsset", label: "Restore Assets" },
      { key: "bulkUpload", label: "Bulk Upload" },
      { key: "viewCredits", label: "View Credits" },
      { key: "allocateCredits", label: "Allocate Credits" },
      { key: "transferCredits", label: "Transfer Credits" },
      { key: "viewCreditHistory", label: "View Credit History" },
      { key: "manageCredits", label: "Manage Credits" },
      { key: "requestCredits", label: "Request Credits" },
      { key: "purchaseLicense", label: "Purchase License" },
    ],
  },
  {
    title: "Tasks And Other",
    items: [
      { key: "createTask", label: "Create Task" },
      { key: "assignTask", label: "Assign Task" },
      { key: "viewAllTasks", label: "View All Tasks" },
      { key: "viewMyTasks", label: "View My Tasks" },
      { key: "updateTask", label: "Update Task" },
      { key: "deleteTask", label: "Delete Task" },
      { key: "messageManagement", label: "Messages" },
    ],
  },
];

const Card = ({ title, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: 2,
      bgcolor: "background.paper",
    }}
  >
    <Stack spacing={1.25}>
      <Typography variant="subtitle1" fontWeight={800}>
        {title}
      </Typography>
      <Divider />
      {children}
    </Stack>
  </Paper>
);

const CollapsibleSection = ({ title, subtitle, expanded, onChange, children, defaultExpanded = false }) => (
  <Accordion
    disableGutters
    expanded={expanded}
    onChange={onChange}
    defaultExpanded={defaultExpanded}
    sx={(t) => ({
      borderRadius: 2,
      overflow: "hidden",
      border: `1px solid ${alpha(t.palette.divider, 0.85)}`,
      boxShadow: "none",
      "&:before": { display: "none" },
      bgcolor: alpha(t.palette.common.white, 0.98),
    })}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
      sx={(t) => ({
        minHeight: 48,
        px: 1.5,
        py: 0.25,
        bgcolor: alpha(t.palette.common.black, 0.03),
        borderBottom: expanded ? `1px solid ${alpha(t.palette.divider, 0.7)}` : "none",
        "& .MuiAccordionSummary-content": {
          my: 0.5,
        },
      })}
    >
      <Box>
        <Typography fontWeight={800} fontSize="0.96rem">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</AccordionDetails>
  </Accordion>
);

const buildRolePermissionState = (role, actorUser = null) => {
  const defaults = getPermissions(role);
  const selected = {};
  PERMISSION_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      selected[item.key] = defaults[item.key] === true;
    });
  });
  return clampAppPermissionsByActor(selected, actorUser);
};

const sanitizeHiddenPermissions = (appPermissions = {}, hiddenPermissionKeys = new Set()) => {
  const nextPermissions = { ...(appPermissions || {}) };
  hiddenPermissionKeys.forEach((key) => {
    nextPermissions[key] = false;
  });
  return nextPermissions;
};

const toLegacyCrudPermissions = (appPermissions) => ({
  devicePermissions: {
    read: !!appPermissions.viewDevices,
    create: !!appPermissions.createDevice,
    update: !!appPermissions.editDevice,
    delete: !!appPermissions.deleteDevice,
    command: !!appPermissions.sendCommands,
  },
  userPermissions: {
    read: !!appPermissions.viewUsers,
    create: !!appPermissions.createUser,
    update: !!appPermissions.editUser,
    delete: !!appPermissions.deleteUser,
    assign: !!appPermissions.assignUserToGA,
  },
});

const buildInitialFormState = (role, initialData = null, actorUser = null) => {
  const initialRole = initialData?.role || role || "";
  const fallbackPermissions = buildRolePermissionState(
    initialRole || ROLES.FIELD_EMPLOYEE,
    actorUser
  );
  const mergedPermissions = clampAppPermissionsByActor(
    {
      ...fallbackPermissions,
      ...(initialData?.permissions?.app || {}),
    },
    actorUser
  );
  return {
    username: initialData?.username || "",
    uniqueId: initialData?.uniqueId || "",
    email: initialData?.email || "",
    mobileNo: initialData?.mobileNo || "",
    password: "",
    role: initialRole,
    appPermissions: mergedPermissions,
  };
};

function CreateUserModal({
  setShowCreateUserModal,
  currentUserRole,
  currentUser,
  mode = "create",
  initialData = null,
  onSuccess,
  allowedRoles: allowedRolesOverride = null,
  hiddenPermissionKeys = null,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [createUser] = useCreateUserMutation();
  const [editUser] = useEditUserMutation();

  const effectiveHiddenPermissionKeys = useMemo(
    () => (hiddenPermissionKeys instanceof Set ? hiddenPermissionKeys : new Set(hiddenPermissionKeys || [])),
    [hiddenPermissionKeys]
  );

  const visiblePermissionGroups = useMemo(
    () =>
      PERMISSION_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !effectiveHiddenPermissionKeys.has(item.key)),
      })).filter((group) => group.items.length > 0),
    [effectiveHiddenPermissionKeys]
  );

  const allowedRoles = useMemo(() => {
    const baseRoles = Array.isArray(allowedRolesOverride) && allowedRolesOverride.length
      ? allowedRolesOverride
      : ORG_CREATABLE_BY_ROLE[currentUserRole] || [];
    const safeRoles = baseRoles.filter(Boolean);
    const mergedRoles = Array.from(
      new Set([
        ...safeRoles,
        ...(initialData?.role ? [initialData.role] : []),
      ])
    );
    return mergedRoles;
  }, [allowedRolesOverride, currentUserRole, initialData?.role]);

  const [form, setForm] = useState(() =>
    ({
      ...buildInitialFormState(allowedRoles[0] || ROLES.FIELD_EMPLOYEE, initialData, currentUser),
      appPermissions: sanitizeHiddenPermissions(
        buildInitialFormState(allowedRoles[0] || ROLES.FIELD_EMPLOYEE, initialData, currentUser).appPermissions,
        effectiveHiddenPermissionKeys
      ),
    })
  );
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    permissions: false,
  });

  const initialDataResetKey = [
    mode,
    initialData?._id || "",
    initialData?.role || "",
    initialData?.username || "",
    initialData?.uniqueId || "",
    initialData?.email || "",
    initialData?.mobileNo || "",
  ].join("|");

  useEffect(() => {
    const nextRole = initialData?.role || allowedRoles[0] || "";
    const nextState = buildInitialFormState(nextRole || ROLES.FIELD_EMPLOYEE, initialData, currentUser);
    setForm({
      ...nextState,
      appPermissions: sanitizeHiddenPermissions(nextState.appPermissions, effectiveHiddenPermissionKeys),
    });
    setError("");
    setExpandedSections({
      details: true,
      permissions: false,
    });
    setShowPassword(false);
  }, [allowedRoles, currentUser, initialDataResetKey, effectiveHiddenPermissionKeys]);

  const toggleSection = (key) => (_, nextExpanded) => {
    setExpandedSections((prev) => ({ ...prev, [key]: nextExpanded }));
  };

  const handleRoleChange = (nextRole) => {
    setForm((prev) => ({
      ...prev,
      role: nextRole,
      appPermissions: sanitizeHiddenPermissions(
        buildRolePermissionState(nextRole, currentUser),
        effectiveHiddenPermissionKeys
      ),
    }));
  };

  const togglePermission = (permissionKey) => {
    if (effectiveHiddenPermissionKeys.has(permissionKey)) return;
    const allowed = hasUserPermission(currentUser, permissionKey);
    if (!allowed) return;

    setForm((prev) => ({
      ...prev,
      appPermissions: {
        ...prev.appPermissions,
        [permissionKey]: !prev.appPermissions[permissionKey],
      },
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.username.trim()) return setError("Name is required");
    if (!form.uniqueId.trim()) return setError("User ID is required");
    if (!form.email.trim()) return setError("Email is required");
    if (mode !== "edit" && !form.password.trim()) return setError("Password is required");
    if (!form.role) return setError("Role is required");

    const clampedAppPermissions = sanitizeHiddenPermissions(
      clampAppPermissionsByActor(form.appPermissions, currentUser),
      effectiveHiddenPermissionKeys
    );
    const { devicePermissions, userPermissions } = toLegacyCrudPermissions(clampedAppPermissions);
    const payload = {
      username: form.username.trim(),
      uniqueId: normalizeUserId(form.uniqueId),
      email: form.email.trim().toLowerCase(),
      mobileNo: String(form.mobileNo || "").trim(),
      ...(form.password?.trim() ? { password: form.password } : {}),
      role: form.role,
      permissions: {
        app: clampedAppPermissions,
      },
      devicePermissions,
      userPermissions,
    };

    try {
      const result =
        mode === "edit" && initialData?._id
          ? await editUser({ userId: initialData._id, ...payload }).unwrap()
          : await createUser(payload).unwrap();

      onSuccess?.(result);
      setShowCreateUserModal(false);
    } catch (submitError) {
      setError(
        submitError?.data?.msg ||
          (mode === "edit" ? "Failed to update user." : "Failed to create user.")
      );
    }
  };

  return (
    <Dialog
      open
      onClose={() => setShowCreateUserModal(false)}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          maxHeight: fullScreen ? "100dvh" : "92vh",
          display: "flex",
          boxShadow: `0 16px 44px ${alpha(theme.palette.common.black, 0.2)}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Stack direction="row" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
            <Users size={20} />
            <Typography sx={{ fontWeight: 1000 }} noWrap>
              {mode === "edit" ? "Edit Organization User" : "Create Organization User"}
            </Typography>
          </Stack>

          <IconButton onClick={() => setShowCreateUserModal(false)} aria-label="Close">
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <form
        onSubmit={onSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <DialogContent
          dividers
          sx={{
            pt: 1,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <CollapsibleSection
              title="User details"
              subtitle="Basic account information and role"
              expanded={expandedSections.details}
              onChange={toggleSection("details")}
              defaultExpanded
            >
              <Card title="Required Information">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Name"
                      value={form.username}
                      onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                      required
                      autoFocus
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="User ID"
                      value={form.uniqueId}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          uniqueId: normalizeUserId(e.target.value),
                        }))
                      }
                      helperText="Only a-z, 0-9, dot, underscore, dash"
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Mobile No."
                      value={form.mobileNo}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          mobileNo: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      inputProps={{ maxLength: 10, inputMode: "numeric" }}
                      required
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                      required={mode !== "edit"}
                      fullWidth
                      helperText={mode === "edit" ? "Leave blank to keep current password" : ""}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              onClick={() => setShowPassword((prev) => !prev)}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="newUserRole-label">Role</InputLabel>
                      <Select
                        labelId="newUserRole-label"
                        value={form.role}
                        label="Role"
                        onChange={(e) => handleRoleChange(e.target.value)}
                      >
                        {allowedRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {ROLE_LABELS[role] || role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Card>
            </CollapsibleSection>

            <CollapsibleSection
              title="Role permissions"
              subtitle="Permission groups controlled by current user access"
              expanded={expandedSections.permissions}
              onChange={toggleSection("permissions")}
            >
              <Card title="Role Permissions">
                <Grid container spacing={2}>
                  {visiblePermissionGroups.map((group) => (
                    <Grid key={group.title} item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
                        <Typography fontWeight={800} sx={{ mb: 1 }}>
                          {group.title}
                        </Typography>

                        <Grid container spacing={1}>
                          {group.items.map((item) => {
                            const canGrant = hasUserPermission(currentUser, item.key);
                            return (
                              <Grid key={item.key} item xs={12} sm={6}>
                                <FormControlLabel
                                  disabled={!canGrant}
                                  control={
                                    <Checkbox
                                      checked={canGrant ? !!form.appPermissions[item.key] : false}
                                      onChange={() => togglePermission(item.key)}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      color={!canGrant ? "text.disabled" : "text.primary"}
                                    >
                                      {item.label}
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </CollapsibleSection>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            sx={{ width: "100%" }}
            justifyContent="flex-end"
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => setShowCreateUserModal(false)}
              sx={{ fontWeight: 900, borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ fontWeight: 900, borderRadius: 2 }}>
              {mode === "edit" ? "Update User" : "Create User"}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CreateUserModal;
